import type { ReactNode } from 'react';

import { createPortal } from 'react-dom';

import { selectActiveDescriptor, useChatDockRegistry } from './useChatDockRegistry';
import { useChatDockStore } from './useChatDockStore';

/**
 * Consumer-rendered wrapper that portals its `children` into the dock's external
 * portal target when this surface (`ownerId`) is the active dock surface.
 *
 * Mounting follows the same lifecycle as the dock's body strategy: a non-default
 * active surface stays mounted while active (open OR closed) so in-flight state
 * survives a collapse (keep-mounted); a default ("floor") active surface mounts
 * only while the dock is open (lazy). When `children` are not rendered they are
 * unmounted, so the consumer's body/engine does not run.
 */
export function DockSlot({ ownerId, children }: { ownerId: string; children: ReactNode }) {
  const descriptors = useChatDockRegistry((s) => s.descriptors);
  const activeOwnerId = useChatDockRegistry((s) => s.activeOwnerId);
  const bodyOpen = useChatDockStore((s) => s.bodyOpen);
  const portalTarget = useChatDockStore((s) => s.portalTarget);

  const resolved = selectActiveDescriptor({ descriptors, activeOwnerId });

  const isActive = resolved?.ownerId === ownerId;
  const isDefault = resolved?.isDefault ?? false;

  // Non-default active surface portals whenever active (open OR closed) — matching
  // keep-mounted. A default-floor active surface portals only while open — matching
  // the lazy unmount-on-close path. Requires a published target to portal into.
  // NOTE: reconciled — the `isDefault && !bodyOpen` clause now reads the
  // collapse-aware `bodyOpen` (= open through the close animation, published by
  // `ChatDockColumn`), matching the dock's own target div. So a default-floor child
  // stays mounted through the collapse and unmounts only after it settles, rather
  // than blinking out mid-animation. Irrelevant for non-default surfaces (the clause
  // never fires, since `isDefault` is false), so their keep-mounted path is unchanged.
  if (!isActive || !portalTarget || (isDefault && !bodyOpen)) {
    return null;
  }
  return createPortal(children, portalTarget);
}
